// src/utils/api.js
import axios from "axios";

const api = axios.create({
  baseURL: "/api",
  timeout: 20000,
  withCredentials: true,
});

// refresh 전용 인스턴스
const refreshApi = axios.create({
  baseURL: "/api",
  timeout: 10000,
  withCredentials: true,
});

let accessToken = localStorage.getItem("accessToken") || null;
let isHardLogoutInProgress = false;

export function getAccessToken() {
  try {
    const stored = localStorage.getItem("accessToken");
    // 메모리에 있는 값이 우선, 없으면 localStorage 값
    return accessToken || stored || null;
  } catch (e) {
    console.warn("[API] getAccessToken localStorage.getItem failed:", e);
    // localStorage 접근 실패해도 최소한 메모리 값은 돌려줌
    return accessToken;
  }
}

// ---- 토큰 세팅 유틸 ----
export const setAccessToken = (t) => {
  console.log("[API] setAccessToken called:", t ? "SET" : "CLEAR");

  accessToken = t;

  if (t) {
    api.defaults.headers.common.Authorization = `Bearer ${t}`;
    try {
      localStorage.setItem("accessToken", t);
    } catch (e) {
      console.warn("[API] localStorage.setItem(accessToken) failed:", e);
    }
  } else {
    delete api.defaults.headers.common.Authorization;
    try {
      localStorage.removeItem("accessToken");
    } catch (e) {
      console.warn("[API] localStorage.removeItem(accessToken) failed:", e);
    }
  }

  try {
    window.dispatchEvent(new Event("access-token-updated"));
  } catch (e) {
    console.warn("[API] dispatchEvent(access-token-updated) failed:", e);
  }
};

if (accessToken) {
  console.log("[API] initial accessToken from localStorage:", accessToken);
  setAccessToken(accessToken);
} else {
  console.log("[API] no initial accessToken in localStorage");
}

// ---- 멀티 탭 동기화 ----
try {
  window.addEventListener("storage", (e) => {
    if (e.key === "accessToken") {
      console.log("[API][storage] accessToken changed:", {
        oldValue: e.oldValue,
        newValue: e.newValue,
      });
      const newToken = e.newValue;
      if (newToken) {
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        accessToken = newToken;
      } else {
        delete api.defaults.headers.common.Authorization;
        accessToken = null;
      }
    } else if (e.key === "global-logout" && e.newValue) {
      console.log("[API][storage] global-logout detected, redirecting to /login");
      accessToken = null;
      delete api.defaults.headers.common.Authorization;
      window.location.replace("/login");
    }
  });
} catch (e) {
  console.warn("[API] addEventListener(storage) failed:", e);
}

// ---- 공통 강제 로그아웃 ----
const hardLogout = ({ callServer = false, showAlert = true } = {}) => {
  if (isHardLogoutInProgress) {
    console.log("[HARD_LOGOUT] already in progress, skip");
    return;
  }
  isHardLogoutInProgress = true;

  console.log("[HARD_LOGOUT] START", {
    callServer,
    showAlert,
    currentAccessToken: accessToken,
    location: typeof window !== "undefined" ? window.location.href : null,
  });

  try {
    if (showAlert) {
      alert("세션이 만료되었습니다. 다시 로그인해 주세요.");
    }
  } catch (e) {
    console.warn("[HARD_LOGOUT] alert failed:", e);
  }

  if (callServer) {
    try {
      console.log("[HARD_LOGOUT] calling /logout to server");
      api.post("/logout").catch((e) => {
        console.warn("[HARD_LOGOUT] /logout call failed:", e);
      });
    } catch (e) {
      console.warn("[HARD_LOGOUT] api.post('/logout') threw:", e);
    }
  }

  accessToken = null;
  delete api.defaults.headers.common.Authorization;

  try {
    localStorage.removeItem("accessToken");
  } catch (e) {
    console.warn("[HARD_LOGOUT] localStorage.removeItem(accessToken) failed:", e);
  }

  try {
    localStorage.setItem("global-logout", String(Date.now()));
  } catch (e) {
    console.warn("[HARD_LOGOUT] localStorage.setItem(global-logout) failed:", e);
  }

  console.log("[HARD_LOGOUT] redirecting to /login");
  try {
    window.location.replace("/login");
  } catch (e) {
    console.error("[HARD_LOGOUT] window.location.replace failed:", e);
  }
};

// ---- refresh 중복 방지 ----

const refreshWaiters = [];
let refreshPromise = null;

export function callRefresh() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const prevToken = getAccessToken(); // localStorage 등

    try {
      const res = await refreshApi.post("/refresh", {}, { withCredentials: true });
      const newToken = res.data?.accessToken;

      if (!newToken) {
        console.log("[REFRESH] no accessToken in response -> treat as failure");
        throw new Error("No accessToken in refresh response");
      }

      setAccessToken(newToken);
      return newToken;
    } catch (err) {
      const status = err?.response?.status;
      const backendCode = err?.response?.data?.code;

      console.log("[REFRESH] error", { status, backendCode, err });

      if (status === 401) {
        const storedAfter = getAccessToken();
        if (storedAfter && storedAfter !== prevToken) {
          console.log("[REFRESH] 401 but newer token found in storage -> adopt it");
          return storedAfter; // ★ 다른 탭이 이미 refresh 성공한 케이스
        }

        console.log("[REFRESH] 401 and no newer token in storage -> real session end");
        // ★★★★★ 여기서 반드시 throw ★★★★★
        throw err;
      }

      // 그 외 에러도 다 interceptor에서 처리하게 throw
      throw err;
    } finally {
      console.log("[REFRESH] finally, clearing promise");
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}


// ---- 인터셉터 ----
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error || {};

    console.log("[API-INTERCEPTOR] onError called", {
      hasResponse: !!response,
      hasConfig: !!config,
      rawError: error,
    });

    if (!response || !config) {
      console.log("[API-INTERCEPTOR] no response or config, just reject");
      return Promise.reject(error);
    }

    const status = response.status;
    const url = config.url || "";
    const data = response.data;
    const headers = response.headers || {};
    const authReason = headers["x-auth-reason"] || headers["X-Auth-Reason"];

    const isLogin = url.includes("/login");
    const isRefresh = url.includes("/refresh");
    const isLogout = url.includes("/logout");

    // 백엔드 JSON { code, message } 추출
    const backendCode = data?.code || data?.errorCode || null;

    console.log("[API-INTERCEPTOR] normalized error info", {
      status,
      url,
      data,
      backendCode,
      authReason,
      isLogin,
      isRefresh,
      isLogout,
    });

    if (isRefresh) {
      console.log("[API-INTERCEPTOR] error from /refresh itself -> pass to caller");
      // /refresh 호출 자체의 에러는 callRefresh에서 처리
      return Promise.reject(error);
    }

    if (status === 401) {
      console.log("[API-INTERCEPTOR] 401 detected for url:", url);

      // 로그인 API 401은 그대로 처리 (폼에서 메시지 보여주기 용)
      if (isLogin) {
        console.log("[API-INTERCEPTOR] 401 from /login -> no auto-logout, just reject");
        return Promise.reject(error);
      }

      // 🔹 “바로 하드 로그아웃해야 하는 401” 구분
      // - LOGGED_OUT: 블랙리스트/강제 로그아웃/비밀번호 변경 등
      // - ACCESS_TOKEN_INVALID: 서명 오류, typ mismatch 등 수습 불가
      // - UNAUTHORIZED_ERROR: 서버 에러성 401
      const shouldHardLogoutDirect =
        backendCode === "LOGGED_OUT" ||
        backendCode === "ACCESS_TOKEN_INVALID" ||
        backendCode === "UNAUTHORIZED" ||
        backendCode === "UNAUTHORIZED_ERROR";

      console.log("[API-INTERCEPTOR] 401 analysis", {
        backendCode,
        authReason,
        shouldHardLogoutDirect,
        _retry: config._retry,
      });

      if (shouldHardLogoutDirect && !isLogout) {
        console.log(
          "[API-INTERCEPTOR] hard-logout type 401 detected -> calling hardLogout()",
          backendCode
        );
        hardLogout({ callServer: false });
        return Promise.reject(error);
      }

      // 나머지 401 중에서, refresh로 복구 가능한 것은 ACCESS_TOKEN_EXPIRED 로 간주
      const isAccessExpired =
        backendCode === "ACCESS_TOKEN_EXPIRED" || backendCode == null;

      console.log("[API-INTERCEPTOR] isAccessExpired?", {
        isAccessExpired,
        backendCode,
      });
if (isAccessExpired && !config._retry && !isLogout) {
  config._retry = true;

  console.log("[API-INTERCEPTOR] trying refresh flow for 401", {
    url,
    backendCode,
  });

  try {
    const newToken = await callRefresh();

    console.log("[API-INTERCEPTOR] refresh returned newToken:", !!newToken);

    // 🔴 새 토큰이 없으면 = refresh 실패로 간주하고 바로 hardLogout
    if (!newToken) {
      console.log(
        "[API-INTERCEPTOR] refresh returned falsy token -> hardLogout()"
      );
      hardLogout({ callServer: false });
      return Promise.reject(error);
    }

    // 🔹 정상적으로 새 토큰을 받은 경우에만 재시도
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${newToken}`;

    console.log("[API-INTERCEPTOR] retrying original request:", url);
    return api(config); // 원래 요청 재시도
  } catch (refreshErr) {
    const refreshStatus =
      refreshErr.refreshStatus ?? refreshErr.response?.status;

    const stored = (() => {
      try {
        return localStorage.getItem("accessToken");
      } catch {
        return null;
      }
    })();

    console.log("[API-INTERCEPTOR] refresh failed", {
      refreshStatus,
      stored,
      memoryAccessToken: accessToken,
      refreshErr,
    });

    if (refreshStatus === 401) {
      // 🔹 같은 브라우저 다른 탭에서 이미 토큰 갱신한 케이스
      if (stored && stored !== accessToken) {
        console.log(
          "[API-INTERCEPTOR] same-browser race detected, adopt stored token"
        );
        setAccessToken(stored);
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${stored}`;
        console.log(
          "[API-INTERCEPTOR] retrying original request with stored token"
        );
        return api(config);
      }

      console.log(
        "[API-INTERCEPTOR] 401 refresh & no newer token -> hardLogout()"
      );
      hardLogout({ callServer: false });
      return Promise.reject(refreshErr);
    }

    console.log(
      "[API-INTERCEPTOR] non-401 refresh failure -> hardLogout()",
      refreshStatus
    );
    hardLogout({ callServer: false });
    return Promise.reject(refreshErr);
  }
}


      console.log(
        "[API-INTERCEPTOR] 401 but no refresh/hardLogout path taken, just reject"
      );
      // 여기까지 왔으면 refresh도 안 하고 그냥 넘김
      return Promise.reject(error);
    }

    // 401 이외의 에러
    console.log("[API-INTERCEPTOR] non-401 error, just reject", {
      status,
      url,
    });
    return Promise.reject(error);
  }
);

// ---- 명시적 로그아웃 ----
export const logout = async () => {
  console.log("[LOGOUT] manual logout requested");
  try {
    await api.post("/logout");
    console.log("[LOGOUT] /logout called successfully");
  } catch (e) {
    console.warn("[LOGOUT] /logout call failed:", e);
  }

  hardLogout({ callServer: false, showAlert: false });
};

export default api;
